/**
 * "Other providers" comparison page (Jaron, #development-dpt Oct 15-16,
 * ticket 13312401562): the customer's current provider (Brinks by default,
 * name editable) LEASES the equipment and charges monitoring that goes up
 * every year; with HomeCam the customer OWNS it.
 *
 * Dependency-free on purpose, like `commission.ts`: everything under
 * `common/helpers` that imports `../env` cannot be unit tested, and these are
 * figures a rep reads out to a customer.
 *
 * Wording: the page says "provider", never "alarm" or "security" - both are
 * banned on anything the customer reads (Monday 12824335937, T56).
 */

/** What the page shows until the rep types the provider's name. */
export const DEFAULT_OTHER_PROVIDER_NAME = 'Brinks'

/** Monthly monitoring assumed until the rep types it (Jaron's sketch: $65). */
export const DEFAULT_OTHER_PROVIDER_MONTHLY = 65

/** Yearly increase of the other provider's monitoring (Jaron: "3% inflation"). */
export const OTHER_PROVIDER_YEARLY_INCREASE = 0.03

/** The inflation, in percent, the page assumes until the rep types one. */
export const DEFAULT_OTHER_PROVIDER_INFLATION_PERCENT =
  OTHER_PROVIDER_YEARLY_INCREASE * 100

/** The highest yearly inflation, in percent, the page accepts as typed. */
export const MAX_OTHER_PROVIDER_INFLATION_PERCENT = 25

/**
 * Where the global on/off switch of the whole comparison lives: a child of
 * `Misc/env`, read with the env hook and written from the admin panel.
 */
export const OTHER_PROVIDER_COMPARISON_ENV_KEY =
  'otherProviderComparisonEnabled'

/** The horizons the page totals (Jaron's sketch: 5, 10 and 15 years). */
export const COMPARISON_YEARS = [5, 10, 15] as const

/**
 * The provider name to show: what the rep typed, or Brinks. Trimmed, so a
 * stray space does not blank the column header.
 */
export const otherProviderName = (raw: unknown): string => {
  const name = typeof raw === 'string' ? raw.trim() : ''
  return name || DEFAULT_OTHER_PROVIDER_NAME
}

/**
 * The number in what the rep typed (the one after a `$` if there is one):
 * "$1,200" is 1200, "65/mo" is 65 and "1,5" is 1.5 (a comma not followed by
 * exactly three digits is a decimal comma, never a thousands separator). NaN
 * when there is no number or a minus sign.
 */
const typedAmount = (text: string): number => {
  if (text.includes('-')) return Number.NaN
  // "Brinks 24/7 $45": the amount is what follows the dollar sign.
  const afterDollar = text.includes('$') ? text.slice(text.indexOf('$')) : text
  const token = /\d[\d,]*(?:\.\d+)?|\.\d+/.exec(
    afterDollar.replace(/[$\s]/g, ''),
  )
  if (!token) return Number.NaN
  const digits = token[0]
  return Number(
    /^\d+,\d{1,2}$/.test(digits)
      ? digits.replace(',', '.')
      : digits.replace(/,/g, ''),
  )
}

/**
 * The provider's monthly monitoring: the rep's figure when it is a positive
 * number ("$65", "65.00", "65" and "65/mo" all read as 65), else the $65
 * default.
 */
export const otherProviderMonthly = (raw: unknown): number => {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
      ? typedAmount(raw)
      : Number.NaN
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_OTHER_PROVIDER_MONTHLY
}

/**
 * The yearly inflation, in percent, the rep typed: "3", "3%" and "3.5" read as
 * typed and "0" is valid (a flat price). Blank, no number, a minus sign or
 * more than 25% fall back to the 3% default.
 */
export const otherProviderInflationPercent = (raw: unknown): number => {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
      ? typedAmount(raw)
      : Number.NaN
  return Number.isFinite(n) &&
    n >= 0 &&
    n <= MAX_OTHER_PROVIDER_INFLATION_PERCENT
    ? n
    : DEFAULT_OTHER_PROVIDER_INFLATION_PERCENT
}

/**
 * The yearly increase as a fraction (3% is 0.03), the form
 * `monitoringOverYears` takes.
 */
export const otherProviderInflation = (raw: unknown): number =>
  otherProviderInflationPercent(raw) / 100

/**
 * Everything paid for monitoring over `years`, with the monthly going up by
 * `yearlyIncrease` at the start of every year after the first:
 * 12·m + 12·m·1.03 + 12·m·1.03² + … (3% unless the rep typed another)
 *
 * $65/mo → 5 yrs $4,141 · 10 yrs $8,942 · 15 yrs $14,507. Jaron's sketch had
 * rounder figures ($4,000 / $10,000 / $17,000); these are the arithmetic of
 * the same rule, so the page never shows a number nobody can reproduce.
 */
export const monitoringOverYears = (
  monthly: number,
  years: number,
  yearlyIncrease: number = OTHER_PROVIDER_YEARLY_INCREASE,
): number => {
  if (!(monthly > 0) || !(years > 0)) return 0
  let total = 0
  for (let y = 0; y < years; y++) {
    total += 12 * monthly * Math.pow(1 + yearlyIncrease, y)
  }
  return total
}

/**
 * The lines of a multi-line text field (equipment, issues): one item per
 * line, trimmed, empty lines and leading bullet marks ("-", "•", "*") dropped,
 * so pasted lists read the same as typed ones.
 */
export const textLines = (raw: unknown): string[] =>
  (typeof raw === 'string' ? raw : '')
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-•*]\s*/, '').trim())
    .filter((line) => line.length > 0)

/**
 * The global switch (`Misc/env/otherProviderComparisonEnabled`). Off ONLY when
 * set to false, "no" or "off" (any case); a missing key or anything else is
 * on, so nothing changes until someone flips it.
 */
export const comparisonFeatureEnabled = (raw: unknown): boolean => {
  if (raw === false) return false
  if (typeof raw !== 'string') return true
  const text = raw.trim().toLowerCase()
  return !(text === 'no' || text === 'off' || text === 'false')
}

/** True once the rep turned the page on for this customer. */
export const otherProviderComparisonOn = (raw: unknown): boolean =>
  raw === 'yes'

/**
 * The pieces of equipment the rep picks from on the app's Alarm tab (Jaron's
 * mockup, Oct 18). These are the rep's labels and are stored as `type`; the
 * customer never reads them, see `otherProviderEquipmentLabel`.
 */
export const OTHER_PROVIDER_EQUIPMENT_TYPES = [
  'Alarm Panel',
  'Entry Sensor',
  'Motion',
  'Glassbreak',
  'Security Camera',
  'Doorbell Camera',
  'Smoke',
  'Carbon',
  'Medical Pendant',
] as const

/**
 * What the customer reads on the comparison page: "alarm" and "security" are
 * banned on anything the customer sees (Monday 12824335937, T56), so the two
 * labels that carry them read differently. Anything else the rep typed (a
 * legacy free-text line) is shown as typed.
 */
const CUSTOMER_EQUIPMENT_LABELS: Record<string, string> = {
  'Alarm Panel': 'Control Panel',
  Carbon: 'Carbon Monoxide',
  Glassbreak: 'Glass Break',
  Motion: 'Motion Sensor',
  'Security Camera': 'Camera',
}

export const otherProviderEquipmentLabel = (type: string): string =>
  CUSTOMER_EQUIPMENT_LABELS[type] ?? type

/** One line of the other provider's equipment. */
export interface OtherProviderEquipmentRow {
  id: string
  /** photo of the piece at the customer's home, a Storage download URL */
  photo: string
  qty: number
  /** one of OTHER_PROVIDER_EQUIPMENT_TYPES, or free text from a legacy line */
  type: string
}

const MAX_EQUIPMENT_QTY = 99

const cleanQty = (raw: unknown): number => {
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) && n >= 1
    ? Math.min(Math.floor(n), MAX_EQUIPMENT_QTY)
    : 1
}

/**
 * The rows stored in `other_provider_equipment_rows` (JSON), or null when the
 * field does not hold a list (never set, or garbage). An empty list is a real
 * value: the rep removed every row.
 */
export const parseEquipmentRows = (
  raw: unknown,
): OtherProviderEquipmentRow[] | null => {
  if (typeof raw !== 'string' || raw.trim() === '') return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!Array.isArray(parsed)) return null
  const rows: OtherProviderEquipmentRow[] = []
  parsed.forEach((item, i) => {
    if (item === null || typeof item !== 'object') return
    const o = item as Record<string, unknown>
    const type = typeof o['type'] === 'string' ? o['type'].trim() : ''
    const photo =
      typeof o['photo'] === 'string' && /^https:\/\//.test(o['photo'])
        ? o['photo']
        : ''
    rows.push({
      id: typeof o['id'] === 'string' && o['id'] ? o['id'] : `row-${i}`,
      photo,
      qty: cleanQty(o['qty']),
      type,
    })
  })
  return rows
}

export const serializeEquipmentRows = (
  rows: readonly OtherProviderEquipmentRow[],
): string => JSON.stringify(rows)

/**
 * The text lines the page showed before the rows existed ("3 x Door",
 * "Smart Panel") as rows: "3 x Door" is quantity 3 of "Door".
 */
export const legacyEquipmentRows = (
  text: unknown,
): OtherProviderEquipmentRow[] =>
  textLines(text).map((line, i) => {
    const m = /^(\d+)\s*x\s*(.+)$/i.exec(line)
    return {
      id: `legacy-${i}`,
      photo: '',
      qty: m ? cleanQty(m[1]) : 1,
      type: m ? (m[2] as string).trim() : line,
    }
  })

/**
 * The equipment rows to use: the rows field when it holds a list (even an
 * empty one), else the legacy text. The legacy text is never deleted, so a
 * build without rows keeps showing it.
 */
export const equipmentRowsFor = (
  rowsRaw: unknown,
  legacyText: unknown,
): OtherProviderEquipmentRow[] =>
  parseEquipmentRows(rowsRaw) ?? legacyEquipmentRows(legacyText)

/** "3 x Door" / "Smart Panel": how a row reads to the customer. */
export const equipmentRowText = (row: OtherProviderEquipmentRow): string => {
  const label = otherProviderEquipmentLabel(row.type)
  return row.qty > 1 ? `${row.qty} x ${label}` : label
}

export const newEquipmentRowId = (): string =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`
